import { type QuerySourceListItem, type QuerySourceListQuery } from '@utazzo/shared';
import { UzStatusBadge } from '@utazzo/ui';
import React from 'react';

import { QuerySourceFormDrawer } from '../components/QuerySourceFormDrawer';
import { listQuerySources } from '../query-sources.api';
import {
  useBulkDeleteQuerySources,
  useBulkSetQuerySourcesStatus,
  useDeleteQuerySource,
  useQuerySourcesList,
} from '../use-query-sources';

import { type DataGridColumn, type DataGridFilter } from '@/components/data-grid';
import { ListScreen, type ListScreenColumns } from '@/components/list-screen';
import { useCanExportFinanceData, useCurrentUser } from '@/features/auth/use-auth';

const STATUS_OPTIONS = [
  { value: '', label: 'Active + inactive' },
  { value: 'active', label: 'Active only' },
  { value: 'inactive', label: 'Inactive only' },
] as const;

/** Server search only fires from this many characters. */
const MIN_SEARCH_LENGTH = 3;

const FILTERS: ReadonlyArray<DataGridFilter> = [
  {
    id: 'status',
    label: 'Status',
    options: STATUS_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-48',
  },
];

export const QuerySourcesListPage = (): React.JSX.Element => {
  const me = useCurrentUser();
  const canExportFinance = useCanExportFinanceData();
  const canDelete = me?.role === 'ADMIN';
  const deleteQuerySource = useDeleteQuerySource();
  const bulkDeleteMut = useBulkDeleteQuerySources();
  const bulkSetStatusMut = useBulkSetQuerySourcesStatus();

  const columns: ListScreenColumns<QuerySourceListItem> = ({ state }) => {
    const cols: ReadonlyArray<DataGridColumn<QuerySourceListItem>> = [
      {
        id: 'serial',
        header: 'S.No',
        width: '64px',
        cell: (_row, index) => (
          <span className="text-zinc-500">{(state.page - 1) * state.pageSize + index + 1}.</span>
        ),
      },
      {
        id: 'name',
        header: 'Name',
        sortable: true,
        cell: (row) => (
          <div className="flex flex-col">
            <span className="font-medium text-zinc-900">{row.name}</span>
            <span className="text-xs text-zinc-500">{row.code}</span>
          </div>
        ),
      },
      {
        id: 'description',
        header: 'Description',
        cell: (row) => <span className="text-zinc-600">{row.description ?? '-'}</span>,
      },
      {
        id: 'sortOrder',
        header: 'Sort',
        sortable: true,
        width: '80px',
        cell: (row) => <span>{row.sortOrder}</span>,
      },
      {
        id: 'status',
        header: 'Status',
        cell: (row) => <UzStatusBadge status={row.isActive ? 'active' : 'inactive'} />,
      },
    ];
    return cols;
  };

  return (
    <ListScreen<QuerySourceListItem, QuerySourceListQuery>
      title="Query Sources"
      description="Where a lead originated (e.g. website, referral, walk-in)."
      useList={useQuerySourcesList}
      defaultSort={{ by: 'sortOrder', dir: 'asc' }}
      minSearchLength={MIN_SEARCH_LENGTH}
      toQuery={(state, base) => {
        const status = state.filters.status;
        return {
          ...base,
          ...(status === 'active' ? { isActive: true } : {}),
          ...(status === 'inactive' ? { isActive: false } : {}),
        };
      }}
      columns={columns}
      filters={FILTERS}
      getRowId={(row) => row.id}
      createButton={{ label: 'Add query source' }}
      renderDrawer={({ open, mode, row, close }) => (
        <QuerySourceFormDrawer
          open={open}
          onOpenChange={(o) => {
            if (!o) close();
          }}
          mode={mode}
          querySource={row}
        />
      )}
      rowActions={{
        edit: true,
        delete: {
          onDelete: (row) => deleteQuerySource.mutateAsync(row.id),
          disabled: () => !canDelete,
          confirm: (row) => ({
            title: 'Delete this query source?',
            description: `${row.name} will be soft-deleted and hidden from lists. Leads already classified with it are unaffected.`,
          }),
        },
      }}
      bulkActions={{
        delete: {
          onDelete: async (rows) => {
            await bulkDeleteMut.mutateAsync(rows.map((r) => r.id));
          },
          confirm: (rows) => ({
            title: `Delete ${rows.length} query source${rows.length === 1 ? '' : 's'}?`,
            description: 'These query sources will be soft-deleted and hidden from lists.',
          }),
        },
        setActive: {
          onSetActive: async (rows, isActive) =>
            bulkSetStatusMut.mutateAsync({ ids: rows.map((r) => r.id), isActive }),
          confirm: (rows, isActive) => ({
            title: `${isActive ? 'Activate' : 'Deactivate'} ${rows.length} query source${rows.length === 1 ? '' : 's'}?`,
            description: isActive
              ? 'They will become selectable again in lead capture and reports.'
              : 'They will be hidden from new selections but remain on historical records.',
          }),
        },
      }}
      export={{
        fetchPage: (q) => listQuerySources(q),
        filename: 'query-sources',
        canExport: canExportFinance,
        csv: [
          { header: 'Code', value: (r) => r.code },
          { header: 'Name', value: (r) => r.name },
          { header: 'Description', value: (r) => r.description ?? '' },
          { header: 'Sort order', value: (r) => r.sortOrder },
          { header: 'Status', value: (r) => (r.isActive ? 'Active' : 'Inactive') },
          { header: 'Legacy ID', value: (r) => r.legacyId ?? '' },
          { header: 'Created at', value: (r) => r.createdAt },
        ],
      }}
      copy={{
        searchPlaceholder: 'Search by name or code (min 3 characters)',
        emptyTitle: 'No query sources match these filters',
        emptyDescription: 'Adjust the status filter or clear the search box.',
        errorTitle: 'Failed to load query sources',
        errorDescription: 'The server returned an error. Try refreshing or check your network.',
      }}
    />
  );
};
